import React, { useEffect, useRef } from 'react';

const SAFE_TAGS = new Set(['P', 'DIV', 'SPAN', 'H1', 'H2', 'H3', 'H4', 'STRONG', 'B', 'EM', 'I', 'U', 'UL', 'OL', 'LI', 'BR', 'HR', 'SECTION', 'TABLE', 'TBODY', 'TR', 'TD', 'TH']);
const SAFE_STYLES = ['font-family', 'font-size', 'font-weight', 'font-style', 'line-height', 'letter-spacing', 'text-align', 'text-decoration', 'color', 'background-color', 'margin', 'margin-top', 'margin-bottom', 'margin-left', 'margin-right', 'padding', 'padding-top', 'padding-bottom', 'padding-left', 'padding-right', 'border', 'border-bottom', 'border-top', 'border-collapse', 'width', 'max-width', 'vertical-align'];

// Create an immutable document tree. Editing changes only text-node values.
// No HTML from clipboard, editor commands, or generated replacements is accepted.
export function parseTextDocument(html) {
  const template = document.createElement('template');
  template.innerHTML = html || '';
  template.content.querySelectorAll('script,style,iframe,object,embed,form,input,button,img,svg,math').forEach((node) => node.remove());
  let nextId = 0;
  const read = (node) => {
    if (node.nodeType === 3) return { id: `text-${nextId++}`, text: node.textContent, editable: Boolean(node.textContent.trim()) };
    if (node.nodeType !== 1) return null;
    const style = {};
    SAFE_STYLES.forEach((property) => {
      const value = node.style.getPropertyValue(property);
      if (value && !/url\s*\(|expression|var\s*\(/i.test(value)) style[property.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase())] = value;
    });
    return { tag: SAFE_TAGS.has(node.tagName) ? node.tagName.toLowerCase() : 'span', style, children: Array.from(node.childNodes).map(read).filter(Boolean) };
  };
  return Array.from(template.content.childNodes).map(read).filter(Boolean);
}

export function replaceDocumentText(tree, id, text) {
  return tree.map((node) => node.id === id ? { ...node, text: String(text) } : node.children ? { ...node, children: replaceDocumentText(node.children, id, text) } : node);
}

export function documentPlainText(tree) {
  return tree.map((node) => node.text !== undefined ? node.text : `${documentPlainText(node.children || [])}${['p', 'div', 'li', 'h1', 'h2', 'h3', 'br'].includes(node.tag) ? '\n' : ''}`).join('');
}

export function documentToHtml(tree) {
  const container = document.createElement('div');
  const append = (nodes, parent) => nodes.forEach((node) => {
    if (node.text !== undefined) { parent.appendChild(document.createTextNode(node.text)); return; }
    const element = document.createElement(node.tag);
    Object.assign(element.style, node.style);
    append(node.children || [], element);
    parent.appendChild(element);
  });
  append(tree, container);
  return container.innerHTML;
}

function EditableText({ node, onChange, index }) {
  const surface = useRef(null);
  useEffect(() => {
    if (surface.current && surface.current.textContent !== node.text) surface.current.textContent = node.text;
  }, [node.text]);
  const paste = (event) => {
    event.preventDefault();
    const text = event.clipboardData.getData('text/plain');
    const selection = window.getSelection();
    if (!selection?.rangeCount) return;
    const range = selection.getRangeAt(0);
    if (!surface.current.contains(range.commonAncestorContainer)) return;
    range.deleteContents();
    const inserted = document.createTextNode(text);
    range.insertNode(inserted);
    range.setStartAfter(inserted);
    range.collapse(true);
    selection.removeAllRanges(); selection.addRange(range);
    onChange(node.id, surface.current.textContent);
  };
  if (!node.editable) return node.text;
  return <span ref={surface} className="opp-editable-text" contentEditable="plaintext-only" suppressContentEditableWarning role="textbox" aria-label={`CV text ${index}`} aria-multiline="true"
    onBeforeInput={(event) => { if ((event.nativeEvent.inputType || '').startsWith('format')) event.preventDefault(); }}
    onKeyDown={(event) => { if ((event.ctrlKey || event.metaKey) && ['b', 'i', 'u'].includes(event.key.toLowerCase())) event.preventDefault(); }}
    onPaste={paste} onDrop={(event) => event.preventDefault()}
    onInput={(event) => onChange(node.id, event.currentTarget.textContent)} />;
}

export default function TextOnlyDocument({ value, onChange }) {
  let textIndex = 0;
  const render = (nodes, path = '') => nodes.map((node, index) => {
    const key = `${path}-${index}`;
    if (node.text !== undefined) return <EditableText key={node.id} node={node} index={++textIndex} onChange={(id, text) => onChange(replaceDocumentText(value, id, text))} />;
    return React.createElement(node.tag, { key, style: node.style }, ['br', 'hr'].includes(node.tag) ? undefined : render(node.children, key));
  });
  return <div className="opp-cv-document" aria-label="Text-only CV editor">{render(value)}</div>;
}
